import { AxiosError, type AxiosResponse } from 'axios';
import { AppError } from './AppError';

export interface IHandleErrorResponseParams {
  /**
   * Error to be thrown.
   */
  error: any;
  /**
   * Prefix to be added to the error message.
   */
  prefix: string;
  /**
   * If the error is operational or not.
   */
  isOperational?: boolean;
  /**
   * Message error to show in the client side.
   */
  clientMessage?: string;
}

const INTERNAL_SERVER_ERROR_STATUS = 500;
const GATEWAY_TIMEOUT_STATUS = 504;

const MAX_DETAILS_LENGTH = 500;

function serializeResponseData(data: unknown): string {
  if (data == null) return '';

  if (typeof data === 'string') return data.trim();

  try {
    return JSON.stringify(data);
  } catch {
    return '';
  }
}

function buildDescription(message: string, details: string): string {
  if (!details || details === message) return message;

  const truncated =
    details.length > MAX_DETAILS_LENGTH
      ? `${details.slice(0, MAX_DETAILS_LENGTH)}...`
      : details;

  return `${message} | upstream: ${truncated}`;
}

export function handleErrorResponse({
  error,
  prefix = '',
  isOperational = false,
  clientMessage,
}: IHandleErrorResponseParams) {
  if (error instanceof AxiosError) {
    let status = INTERNAL_SERVER_ERROR_STATUS;
    let responseData: unknown;

    if (error.response != null) {
      status = (error.response as AxiosResponse).status;
      responseData = (error.response as AxiosResponse).data;
    } else if (error.request != null) {
      status = GATEWAY_TIMEOUT_STATUS;
    }

    return new AppError(
      prefix,
      status,
      buildDescription(error.message, serializeResponseData(responseData)),
      isOperational,
      clientMessage,
      responseData
    );
  }

  return new AppError(
    prefix,
    INTERNAL_SERVER_ERROR_STATUS,
    error.message,
    isOperational,
    clientMessage
  );
}
